import React, { useCallback, useEffect, useRef, useState } from 'react';
import FormInput from '../../../components/common/FormInput/FormInput';
import FormSelect from '../../../components/common/FormSelect/FormSelect';
import Checkbox from '../../../components/common/Checkbox/Checkbox';
import PhotoUploadButton from '../../../components/common/PhotoUploadButton/PhotoUploadButton';
import {
  EQUIPMENT_CATEGORY_LABELS,
  EQUIPMENT_CATEGORY_ORDER,
  type EquipmentCategory,
} from '../../../constants/equipmentCategory';
import {
  EquipmentCatalogService,
  MAX_EQUIPMENT_IMAGES,
  type EquipmentCatalogPayload,
  type EquipmentCatalogRow,
  type EquipmentImage,
} from '../../../services';
import styles from './EquipmentModal.module.css';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';

const CATEGORY_OPTIONS = EQUIPMENT_CATEGORY_ORDER.map((category) => ({
  value: category,
  label: EQUIPMENT_CATEGORY_LABELS[category],
}));

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

interface FormState {
  name: string;
  category: EquipmentCategory;
  isActive: boolean;
}

interface FormErrors {
  name?: string;
  category?: string;
  images?: string;
}

interface PendingImage {
  key: string;
  file: File;
  previewUrl: string;
}

interface EquipmentModalProps {
  open: boolean;
  equipment: EquipmentCatalogRow | null;
  loading: boolean;
  canManage?: boolean;
  onConfirm: (payload: EquipmentCatalogPayload, pendingFiles: File[]) => Promise<void>;
  onDeactivate: (equipment: EquipmentCatalogRow) => Promise<void>;
  onImagesChanged?: () => void;
  onCancel: () => void;
}

const emptyForm = (): FormState => ({
  name: '',
  category: 'lower_body',
  isActive: true,
});

const equipmentToForm = (equipment: EquipmentCatalogRow): FormState => ({
  name: equipment.name,
  category: equipment.category,
  isActive: equipment.isActive,
});

const validate = (form: FormState): FormErrors => {
  const errors: FormErrors = {};
  if (!form.name.trim()) {
    errors.name = 'Nome é obrigatório.';
  }
  if (!form.category) {
    errors.category = 'Categoria é obrigatória.';
  }
  return errors;
};

const EquipmentModal: React.FC<EquipmentModalProps> = ({
  open,
  equipment,
  loading,
  canManage = true,
  onConfirm,
  onDeactivate,
  onImagesChanged,
  onCancel,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedImages, setSavedImages] = useState<EquipmentImage[]>([]);
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imageBusyPath, setImageBusyPath] = useState<string | null>(null);

  const totalImages = savedImages.length + pendingImages.length;
  const canAddMore = totalImages < MAX_EQUIPMENT_IMAGES;

  const loadSavedImages = useCallback(async (equipmentId: number) => {
    setImagesLoading(true);
    try {
      const images = await EquipmentCatalogService.listImages(equipmentId);
      setSavedImages(images);
    } catch {
      setSavedImages([]);
    } finally {
      setImagesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(equipment ? equipmentToForm(equipment) : emptyForm());
    setErrors({});
    setSaveError(null);
    setPendingImages((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });

    if (equipment) {
      void loadSavedImages(equipment.id);
    } else {
      setSavedImages([]);
    }
  }, [open, equipment, loadSavedImages]);

  useEscapeToClose(onCancel, open && !loading && !imageBusyPath);

  if (!open) {
    return null;
  }

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handlePickImages = () => {
    fileInputRef.current?.click();
  };

  const handleImagesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';

    if (files.length === 0) {
      return;
    }

    const remaining = MAX_EQUIPMENT_IMAGES - totalImages;
    if (remaining <= 0) {
      setErrors((prev) => ({
        ...prev,
        images: `Limite de ${MAX_EQUIPMENT_IMAGES} imagens atingido.`,
      }));
      return;
    }

    const accepted = files
      .filter((file) => ACCEPTED_IMAGE_TYPES.includes(file.type))
      .slice(0, remaining);

    if (accepted.length === 0) {
      setErrors((prev) => ({
        ...prev,
        images: 'Selecione imagens JPG, PNG ou WEBP.',
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, images: undefined }));

    if (equipment) {
      void (async () => {
        setImageBusyPath('upload');
        setSaveError(null);
        try {
          const images = await EquipmentCatalogService.uploadImages(equipment.id, accepted);
          setSavedImages(images);
          onImagesChanged?.();
        } catch (error) {
          setSaveError(error instanceof Error ? error.message : 'Erro ao enviar imagens.');
        } finally {
          setImageBusyPath(null);
        }
      })();
      return;
    }

    setPendingImages((prev) => [
      ...prev,
      ...accepted.map((file, index) => ({
        key: `${file.name}-${file.size}-${Date.now()}-${index}`,
        file,
        previewUrl: URL.createObjectURL(file),
      })),
    ]);
  };

  const handleRemovePending = (key: string) => {
    setPendingImages((prev) => {
      const target = prev.find((item) => item.key === key);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((item) => item.key !== key);
    });
  };

  const handleRemoveSaved = async (storagePath: string) => {
    if (!equipment) {
      return;
    }

    setImageBusyPath(storagePath);
    setSaveError(null);
    try {
      const images = await EquipmentCatalogService.deleteImage(equipment.id, storagePath);
      setSavedImages(images);
      onImagesChanged?.();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Erro ao remover imagem.');
    } finally {
      setImageBusyPath(null);
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaveError(null);
    try {
      await onConfirm(
        {
          name: form.name.trim(),
          category: form.category,
          isActive: form.isActive,
        },
        pendingImages.map((item) => item.file),
      );
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Erro ao salvar. Tente novamente.');
    }
  };

  const title = equipment ? 'Editar equipamento' : 'Novo equipamento';
  const busy = loading || Boolean(imageBusyPath) || imagesLoading;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
        <div className={styles.iconCircle}>
          <span className="material-symbols-outlined">fitness_center</span>
        </div>

        <h3 className={styles.title}>{title}</h3>

        <div className={styles.formGrid}>
          <div className={styles.thumbnailBlock}>
            <div className={styles.galleryHeader}>
              <span className={styles.thumbnailLabel}>Imagens</span>
              <span className={styles.galleryCount}>
                {totalImages}/{MAX_EQUIPMENT_IMAGES}
              </span>
            </div>

            <div className={styles.galleryGrid}>
              {savedImages.map((image) => (
                <div key={image.storagePath} className={styles.galleryItem}>
                  <img src={image.url} alt="" className={styles.galleryImage} />
                  {canManage && (
                    <button
                      type="button"
                      className={styles.galleryRemove}
                      aria-label="Remover imagem"
                      disabled={busy}
                      onClick={() => {
                        void handleRemoveSaved(image.storagePath);
                      }}
                    >
                      <span className="material-symbols-outlined">close</span>
                    </button>
                  )}
                </div>
              ))}

              {pendingImages.map((image) => (
                <div key={image.key} className={styles.galleryItem}>
                  <img src={image.previewUrl} alt="" className={styles.galleryImage} />
                  {canManage && (
                    <button
                      type="button"
                      className={styles.galleryRemove}
                      aria-label="Remover imagem pendente"
                      disabled={busy}
                      onClick={() => handleRemovePending(image.key)}
                    >
                      <span className="material-symbols-outlined">close</span>
                    </button>
                  )}
                </div>
              ))}

              {totalImages === 0 && !imagesLoading && (
                <div className={styles.galleryEmpty}>
                  <span className={`material-symbols-outlined ${styles.thumbnailIcon}`}>image</span>
                  <span>Nenhuma imagem</span>
                </div>
              )}
            </div>

            <div className={styles.thumbnailAction}>
              <PhotoUploadButton
                onClick={handlePickImages}
                disabled={busy || !canManage || !canAddMore}
              >
                Adicionar imagens
              </PhotoUploadButton>
              {errors.images && <span className={styles.inlineError}>{errors.images}</span>}
              {equipment && (
                <span className={styles.galleryHint}>
                  No edit, as imagens são enviadas/removidas na hora.
                </span>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              hidden
              onChange={handleImagesSelected}
            />
          </div>

          <FormInput
            id="equipment-name"
            label="Nome"
            required
            value={form.name}
            onChange={(event) => setField('name', event.target.value)}
            error={errors.name}
            disabled={busy || !canManage}
            placeholder="Ex: Cadeira extensora"
          />

          <FormSelect
            id="equipment-category"
            label="Categoria"
            required
            options={CATEGORY_OPTIONS}
            value={form.category}
            onChange={(event) => setField('category', event.target.value as EquipmentCategory)}
            error={errors.category}
            disabled={busy || !canManage}
          />

          <div className={styles.toggleRow}>
            <Checkbox
              label="Equipamento ativo"
              checked={form.isActive}
              onPress={() => setField('isActive', !form.isActive)}
              disabled={busy || !canManage}
              labelPosition="left"
              className={styles.toggleCheckbox}
              ariaLabel="Equipamento ativo"
              variant="classic"
            />
          </div>
        </div>

        {saveError && <p className={styles.saveError}>{saveError}</p>}

        <div className={styles.actions}>
          {equipment?.isActive && canManage && (
            <button
              className={`${styles.btn} ${styles.btnDeactivate}`}
              onClick={() => {
                void onDeactivate(equipment);
              }}
              disabled={busy}
              type="button"
            >
              Desativar
            </button>
          )}
          <button
            className={`${styles.btn} ${styles.btnCancel}`}
            onClick={onCancel}
            disabled={busy}
            type="button"
          >
            Cancelar
          </button>
          <button
            className={`${styles.btn} ${styles.btnConfirm}`}
            onClick={() => {
              void handleSubmit();
            }}
            disabled={busy || !canManage}
            type="button"
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EquipmentModal;
